import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, Mail, MapPin, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
  full_name: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please add your name, email and a short message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads/contact`, form);
      setForm(INITIAL);
      toast.success("Message sent — we'll reply within 1 business day.");
    } catch (err) {
      console.error(err);
      toast.error("Couldn't send right now. Please email aaron@blackrockhospitality.co.nz.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-36 bg-[#070809] border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <span className="block w-10 h-px bg-[#C2A878]" />
            <span className="text-xs tracking-[0.3em] uppercase text-[#C2A878] font-semibold">
              Get in touch
            </span>
          </div>
          <h2 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-white tracking-tight">
            Let&apos;s talk about your <em className="text-[#C2A878] not-italic">numbers.</em>
          </h2>
          <p className="mt-8 text-white/65 leading-relaxed">
            Prefer direct contact? Reach us below. We reply to every enquiry
            within one business day.
          </p>

          <ul className="mt-12 space-y-6 border-t border-white/10 pt-10">
            <li className="flex items-start gap-4">
              <Mail className="w-4 h-4 text-[#C2A878] mt-1" />
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-white/45 font-semibold">
                  Email
                </div>
                <a
                  href="mailto:aaron@blackrockhospitality.co.nz"
                  data-testid="contact-email-link"
                  className="text-white hover:text-[#C2A878] transition-colors"
                >
                  aaron@blackrockhospitality.co.nz
                </a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Phone className="w-4 h-4 text-[#C2A878] mt-1" />
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-white/45 font-semibold">
                  Phone
                </div>
                <a
                  href="tel:+64275188521"
                  data-testid="contact-phone-link"
                  className="text-white hover:text-[#C2A878] transition-colors"
                >
                  +64 27 518 8521
                </a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <MapPin className="w-4 h-4 text-[#C2A878] mt-1" />
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-white/45 font-semibold">
                  Based in
                </div>
                <div className="text-white/70">
                  Bay of Plenty · Servicing all of New Zealand
                </div>
              </div>
            </li>
          </ul>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="bg-white/[0.025] border border-white/10 p-8 md:p-10 space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ContactField
                label="Full name *"
                value={form.full_name}
                onChange={(v) => update("full_name", v)}
                testid="contact-input-full-name"
                required
              />
              <ContactField
                label="Email *"
                type="email"
                value={form.email}
                onChange={(v) => update("email", v)}
                testid="contact-input-email"
                required
              />
              <ContactField
                label="Phone"
                value={form.phone}
                onChange={(v) => update("phone", v)}
                testid="contact-input-phone"
              />
              <ContactField
                label="Subject"
                value={form.subject}
                onChange={(v) => update("subject", v)}
                testid="contact-input-subject"
              />
            </div>

            <div>
              <label className="block text-[10px] tracking-[0.25em] uppercase text-white/55 font-semibold">
                Message *
              </label>
              <textarea
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                rows={5}
                required
                data-testid="contact-input-message"
                className="mt-2 w-full bg-[#0A0D10] border border-white/15 text-white rounded-none p-4 text-sm placeholder:text-white/30 focus:border-[#C2A878] focus:ring-1 focus:ring-[#C2A878] focus:outline-none transition-colors"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="contact-form-submit"
              className="group inline-flex items-center gap-3 bg-[#C2A878] hover:bg-[#D4AF37] disabled:bg-[#C2A878]/60 text-[#050505] px-8 py-4 text-xs font-semibold tracking-[0.2em] uppercase transition-all duration-300"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  Send message
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

function ContactField({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="block text-[10px] tracking-[0.25em] uppercase text-white/55 font-semibold">
        {label}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full bg-[#0A0D10] border border-white/15 text-white rounded-none px-4 py-3 text-sm placeholder:text-white/30 focus:border-[#C2A878] focus:ring-1 focus:ring-[#C2A878] focus:outline-none transition-colors"
      />
    </div>
  );
}
